/**
 * Unified tool registry. Aggregates all domain-specific tool modules
 * (chart, formula, template, etc.) into a single surface consumed by
 * the LLM tool loop in `excelAIService.ts`.
 *
 * To add a new tool:
 *  1. Create a domain file (e.g. `myNewTools.ts`) exporting the tool
 *     def and an async executor.
 *  2. Import here, add to `ALL_TOOLS`, and register the executor in
 *     `TOOL_EXECUTORS` (keyed by tool name).
 */

import {
  CHART_CAPABILITY_TOOLS,
  executeChartCapabilityTool,
  type ChartCapabilityToolDef,
} from "./chartCapabilityTools";
import {
  GENERATE_FORMULA_TOOL,
  getGenerateFormulaToolFresh,
  executeGenerateFormulaToolDetailed,
} from "./formulaTools";
import {
  TEMPLATE_TOOLS,
  RETRIEVE_TEMPLATE_TOOL,
  APPLY_TEMPLATE_TOOL,
  executeRetrieveTemplateTool,
  executeApplyTemplateTool,
  type TemplateToolContext,
  type ApplyTemplateOutcome,
} from "./templateTools";
import {
  buildGetSkillTool,
  executeGetSkillTool,
  type SkillToolContext,
} from "./excelSkills";
import {
  EXECUTOR_READ_TOOLS,
  executeListSheetsTool,
  executeReadRangeTool,
} from "./executorReadTools";
import {
  ACTIVE_GRAPH_TOOLS,
} from "./chartCreatorTools";
import {
  FILINGS_QUERY_TOOL,
  FINSTER_QUERY_TOOL,
  buildFinsterQueryTool,
  executeFilingsQueryToolDetailed,
  executeFinsterQueryToolDetailed,
} from "./finsterQueryTool";
import {
  ID_LOOKUP_TOOL,
  ID_LOOKUP_TOOL_NAME,
  executeIdLookupToolDetailed,
} from "./idLookupTool";
import {
  isFilingsQueryToolName,
  isResearchToolName,
  type FinsterStreamSubEvent,
} from "../shared/api/finster/queryTool";
import type { FinsterQueryDataSource } from "../shared/api/finster/constants";
import type { ExcelLangfuseRunContext } from "./langfuseTrace";

export type { ChartCapabilityToolDef as ToolDef };
export type { TemplateToolContext, ApplyTemplateOutcome };
export type ToolProvider = "openai" | "anthropic" | "google";

/**
 * Result of a detailed tool dispatch. `output` is the JSON string fed back to
 * the model; `observationMetadata` carries optional extra fields merged into the
 * Langfuse tool observation (e.g. FactSet session / chain id) WITHOUT leaking
 * into the model-facing `output`.
 */
export interface ToolCallDetailedResult {
  output: string;
  observationMetadata?: Record<string, unknown>;
  /** Provider citations retained for UI source cards, never replayed to the model. */
  researchCitations?: unknown[];
}

/**
 * Scope controls which tools are surfaced to each LLM call site:
 *  - "chat"             → retrieve_template + chart + formula + full/focused research
 *                         (no side-effects)
 *  - "planner"          → retrieve_template + chart + formula + get_skill
 *                         (NOT finster_query — research belongs in the executor;
 *                         a planner call would be discarded and re-run, doubling
 *                         a slow network round-trip)
 *  - "executor"         → all tools including apply_template + get_skill +
 *                         both full and filings-focused research
 *  - "react-executor"   → multi-hop executor agent tools (read, write,
 *                         data/calc, control) + shared globals + both research routes
 *
 * The full research tool's public name is brand-dependent (`finster_query` in
 * dev, `factset_ai_query` inside the FactSet host); `filings_query` is stable.
 */
export type ToolScope = "chat" | "planner" | "executor" | "react-executor";

/** All tools available to the LLM tool loop (superset). */
export const ALL_TOOLS: readonly ChartCapabilityToolDef[] = [
  GENERATE_FORMULA_TOOL,
  ...TEMPLATE_TOOLS,
  ...ACTIVE_GRAPH_TOOLS,
  buildGetSkillTool(),
  ID_LOOKUP_TOOL,
  FILINGS_QUERY_TOOL,
  FINSTER_QUERY_TOOL,
];

/**
 * Context passed to tool executors that need runtime state such as template
 * catalogs, skills, or a snapshotted research scope. Call sites that do not
 * need contextual tools can omit this or pass `undefined`.
 */
export interface ToolCallContext {
  templateContext?: TemplateToolContext;
  skillContext?: SkillToolContext;
  researchContext?: {
    dataSources: readonly FinsterQueryDataSource[];
    /** Files explicitly selected in the composer for this run. */
    manualUserFileIds?: readonly string[];
    /** Effective files contributed by resolved Skill policy rather than the composer. */
    skillTargetFileIds?: readonly string[];
    /** Effective union sent to the research service. */
    userFileIds: readonly string[];
    selectionMode?: "default" | "filtered" | "user_files";
    scopeAvailable?: boolean;
    workbookName?: string;
    sheetName?: string;
    sheetNumber?: number;
  };
  onResearchSubEvent?: (event: FinsterStreamSubEvent) => void;
  /** Chat-tab id shared by every research turn in this tab, sent as `session_id`. */
  chatSessionId?: string;
  langfuseContext?: ExcelLangfuseRunContext;
  /** Internal delivery mode; the public research tool schema remains `{ query }`. */
  researchExecutionMode?: "stream" | "buffered";
  requireFormulaQueries?: boolean;
  /**
   * Exact resolved guidance for user-authored Skills, forwarded verbatim to
   * every nested research call. Unrelated to `skillContext`, which carries
   * Excel's built-in modeling skills.
   */
  userSkillGuidance?: string;
}

/**
 * Signature shared by every name-keyed tool executor. Each receives the
 * model's parsed arguments plus the same runtime handles `executeToolCallDetailed`
 * is given, and returns the JSON string fed back to the model.
 *
 * @param rawInput   Parsed JSON arguments from the LLM.
 * @param signal     AbortSignal for cancellation.
 * @param context    Runtime state needed by tools that depend on session- or
 *                   run-scoped data. Optional — tools that do not need it
 *                   (formula, read) ignore it.
 */
type ToolExecutor = (
  rawInput: unknown,
  signal?: AbortSignal,
  context?: ToolCallContext,
) => Promise<string>;

/**
 * Name → executor lookup for all single-name tools. Some tools are dispatched
 * separately in `executeToolCallDetailed`: chart capability tools share one
 * sub-dispatcher (`executeChartCapabilityTool`), and `generate_formula` /
 * the research tool use the detailed channel so they can surface Langfuse
 * observation metadata (FactSet chain / session id).
 *
 * Tools requiring session-scoped context guard for it here and return a
 * structured error (not a throw) when it's missing, so the model can
 * recover gracefully.
 */
const TOOL_EXECUTORS: Record<string, ToolExecutor> = {
  retrieve_template: (rawInput, signal, context) => {
    if (!context?.templateContext) {
      return Promise.resolve(
        JSON.stringify({ error: "Template context unavailable in this session." }),
      );
    }
    return executeRetrieveTemplateTool(rawInput, context.templateContext, signal);
  },

  apply_template: (rawInput, signal, context) => {
    if (!context?.templateContext) {
      return Promise.resolve(
        JSON.stringify({ error: "Template context unavailable in this session." }),
      );
    }
    return executeApplyTemplateTool(rawInput, context.templateContext, signal);
  },

  get_skill: (rawInput, _signal, context) =>
    Promise.resolve(executeGetSkillTool(rawInput, context?.skillContext)),

  list_sheets: (rawInput) => executeListSheetsTool(rawInput),

  read_range: (rawInput) => executeReadRangeTool(rawInput),
};

const RUN_SCOPED_TOOL_NAMES = new Set(ACTIVE_GRAPH_TOOLS.map((tool) => tool.name));

/**
 * Unified async tool dispatcher that also surfaces optional Langfuse
 * observation metadata. Routes by tool name to the correct executor and returns
 * `{ output, observationMetadata? }`. `output` is the JSON string fed back to
 * the model; `observationMetadata` carries trace-only fields (FactSet session /
 * chain id) that the tool loops attach to the Langfuse observation.
 *
 * Unknown tool names produce a structured error (not thrown) so the
 * model can recover gracefully.
 *
 * @param name       Tool name from the LLM's function_call output.
 * @param rawInput   Parsed JSON arguments from the LLM.
 * @param signal     AbortSignal for cancellation.
 * @param context    Runtime state needed by tools that depend on session- or
 *                   run-scoped data. Optional — tools that do not need it
 *                   (chart, formula) ignore it.
 */
export async function executeToolCallDetailed(
  name: string,
  rawInput: unknown,
  signal?: AbortSignal,
  context?: ToolCallContext,
): Promise<ToolCallDetailedResult> {
  if (CHART_CAPABILITY_TOOLS.some((t) => t.name === name)) {
    return { output: await executeChartCapabilityTool(name, rawInput) };
  }
  if (name === ID_LOOKUP_TOOL_NAME) {
    return executeIdLookupToolDetailed(rawInput, signal);
  }
  if (isFilingsQueryToolName(name)) {
    return executeFilingsQueryToolDetailed(rawInput, signal, {
      dataSources: context?.researchContext?.dataSources,
      userFileIds: context?.researchContext?.userFileIds,
      selectionMode: context?.researchContext?.selectionMode,
      scopeAvailable: context?.researchContext?.scopeAvailable,
      workbookName: context?.researchContext?.workbookName,
      sheetName: context?.researchContext?.sheetName,
      sheetNumber: context?.researchContext?.sheetNumber,
      chatSessionId: context?.chatSessionId,
      onSubEvent: context?.onResearchSubEvent,
      langfuseContext: context?.langfuseContext,
      executionMode: context?.researchExecutionMode,
      userSkillGuidance: context?.userSkillGuidance,
    });
  }
  // The research tool's public name is brand-dependent (`finster_query` in dev,
  // `factset_ai_query` inside FactSet), so route by brand rather than a fixed key.
  if (isResearchToolName(name)) {
    return executeFinsterQueryToolDetailed(rawInput, signal, {
      dataSources: context?.researchContext?.dataSources,
      userFileIds: context?.researchContext?.userFileIds,
      selectionMode: context?.researchContext?.selectionMode,
      scopeAvailable: context?.researchContext?.scopeAvailable,
      workbookName: context?.researchContext?.workbookName,
      sheetName: context?.researchContext?.sheetName,
      sheetNumber: context?.researchContext?.sheetNumber,
      chatSessionId: context?.chatSessionId,
      onSubEvent: context?.onResearchSubEvent,
      langfuseContext: context?.langfuseContext,
      executionMode: context?.researchExecutionMode,
      userSkillGuidance: context?.userSkillGuidance,
    });
  }
  if (name === "generate_formula") {
    return executeGenerateFormulaToolDetailed(rawInput, signal, {
      requireQueries: context?.requireFormulaQueries,
    });
  }
  if (RUN_SCOPED_TOOL_NAMES.has(name)) {
    return {
      output: JSON.stringify({
        error:
          `Tool "${name}" is run-scoped and must be executed through the executor runtime ` +
          `so workbook targeting can be applied.`,
      }),
    };
  }
  const executor = TOOL_EXECUTORS[name];
  if (executor) {
    return { output: await executor(rawInput, signal, context) };
  }
  return {
    output: JSON.stringify({
      error: `Unknown tool: "${name}". ` +
        `Available: ${ALL_TOOLS.map((t) => t.name).join(", ")}.`,
    }),
  };
}

/**
 * Provider-neutral tool selection for a scope; the serializers below only
 * change the envelope. Rebuilds the formula and research tools on each call so
 * the embedded date and brand name stay current, and de-duplicates first-wins
 * by name so `extraTools` cannot shadow a global.
 */
function selectToolDefsForScope(
  scope: ToolScope,
  extraTools?: readonly ChartCapabilityToolDef[],
  options: { includeIdLookup?: boolean } = {},
): ChartCapabilityToolDef[] {
  const requireFormulaQueries = scope === "executor" || scope === "react-executor";
  const includeIdLookup =
    options.includeIdLookup === true &&
    (scope === "executor" || scope === "react-executor");
  const freshTools: ChartCapabilityToolDef[] = [
    getGenerateFormulaToolFresh({ requireQueries: requireFormulaQueries }),
    RETRIEVE_TEMPLATE_TOOL,
  ];

  // Research tool: every scope EXCEPT the planner (planner research would be
  // discarded then re-run by the executor, doubling a slow call). Built fresh
  // so the brand-specific name (finster_query / factset_ai_query) is current.
  if (scope !== "planner") {
    freshTools.push(FILINGS_QUERY_TOOL);
    freshTools.push(buildFinsterQueryTool(undefined, { includeIdLookup }));
  }

  if (scope === "executor" || scope === "react-executor") {
    if (includeIdLookup) freshTools.push(ID_LOOKUP_TOOL);
    freshTools.push(APPLY_TEMPLATE_TOOL);
    freshTools.push(...ACTIVE_GRAPH_TOOLS);
  }

  if (scope === "planner" || scope === "executor" || scope === "react-executor") {
    freshTools.push(buildGetSkillTool());
  }

  if (scope === "react-executor") {
    freshTools.push(...EXECUTOR_READ_TOOLS);
  }

  if (extraTools) {
    freshTools.push(...extraTools);
  }

  return freshTools.filter(
    (tool, index, tools) => tools.findIndex((candidate) => candidate.name === tool.name) === index,
  );
}

/** OpenAI Responses API envelope: flat `function` tool with `parameters`. */
function serializeToolForOpenAI(tool: ChartCapabilityToolDef): Record<string, unknown> {
  const def: Record<string, unknown> = {
    type: "function",
    name: tool.name,
    description: tool.description,
    parameters: tool.inputSchema,
  };
  if (tool.strict) {
    def.strict = true;
  }
  return def;
}

/**
 * Anthropic Messages API envelope: `{ name, description, input_schema }` with
 * no `type` wrapper and no `strict` — constrained decoding is OpenAI-only, and
 * Anthropic rejects unknown keys inside `input_schema`.
 */
function serializeToolForAnthropic(tool: ChartCapabilityToolDef): Record<string, unknown> {
  const inputSchema: Record<string, unknown> = { ...tool.inputSchema };
  delete inputSchema.strict;
  return {
    name: tool.name,
    description: tool.description,
    input_schema: inputSchema,
  };
}

/**
 * Serialize tool definitions for a specific LLM provider, filtered by scope.
 * Tool *selection* is shared across providers (`selectToolDefsForScope`); only
 * the serialization envelope differs. OpenAI and Anthropic are wired; the
 * Google stub still returns null.
 *
 * @param provider  LLM provider to serialize for (determines envelope shape).
 * @param scope     Which tool subset to include. Defaults to "executor" (all tools).
 * @param extraTools  Additional tool defs to append (used by react-executor
 *                    to inject run-scoped tools without polluting the global list).
 * @param options     Per-call capabilities. Lookup defaults absent.
 */
export function buildToolDefinitionsForProvider(
  provider: ToolProvider,
  scope: ToolScope = "executor",
  extraTools?: readonly ChartCapabilityToolDef[],
  options: { includeIdLookup?: boolean } = {},
): unknown[] | null {
  if (provider === "openai") {
    return selectToolDefsForScope(scope, extraTools, options).map(serializeToolForOpenAI);
  }
  if (provider === "anthropic") {
    return selectToolDefsForScope(scope, extraTools, options).map(serializeToolForAnthropic);
  }
  if (provider === "google") return null;
  return null;
}
